import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import LoginPage from './pages/auth/LoginPage';
import AppLayout from './layouts/AppLayout';
import ProtectedRoute from './components/ProtectedRoute';
import AppErrorBoundary from './components/shared/AppErrorBoundary';
import LoadingSkeleton from './components/shared/LoadingSkeleton';
import { Roles } from './constants';
import { BROKER_PORTAL_ROLES } from './config/brokerAccess';

const LenderDashboardPage = lazy(() => import('./pages/dashboard/LenderDashboardPage'));
const BrokersListPage = lazy(() => import('./pages/organization/BrokersListPage'));
const BrokerDetailPage = lazy(() => import('./pages/organization/BrokerDetailPage'));
const FeatureManagementPage = lazy(() => import('./pages/admin/FeatureManagementPage'));
const LockDeskSettingsPage = lazy(() => import('./pages/admin/LockDeskSettingsPage'));
const BrandingManagementPage = lazy(() => import('./pages/admin/BrandingManagementPage'));
const LenderUsersPage = lazy(() => import('./pages/organization/LenderUsersPage'));
const VestaContactSyncPage = lazy(() => import('./pages/admin/VestaContactSyncPage'));
const MarketingAdminPage = lazy(() => import('./pages/admin/marketing/MarketingAdminPage'));
const BrokerPipelinePage = lazy(() => import('./pages/pipeline/BrokerPipelinePage'));
const CompensationPage = lazy(() => import('./pages/organization/CompensationPage'));
const CompensationRequestsPage = lazy(() => import('./pages/organization/CompensationRequestsPage'));
const BrokerManageCompanyPage = lazy(() => import('./pages/organization/BrokerManageCompanyPage'));
const LoanDetailPage = lazy(() => import('./pages/loan/LoanDetailPage'));
const ScenarioPricerPage = lazy(() => import('./pages/pricing/ScenarioPricerPage'));
const ActivateAccountPage = lazy(() => import('./pages/auth/ActivateAccountPage'));
const ResetPasswordPage = lazy(() => import('./pages/auth/ResetPasswordPage'));
const BrokerSignupPage = lazy(() => import('./pages/auth/BrokerSignupPage'));
const AeDashboardPage = lazy(() => import('./pages/dashboard/AeDashboardPage'));
const AeComingSoonPage = lazy(() => import('./pages/ae/AeComingSoonPage'));
const AeBookOfBusinessPage = lazy(() => import('./pages/ae/AeBookOfBusinessPage'));
const AeBroker360Page = lazy(() => import('./pages/ae/AeBroker360Page'));
const RetailPipelinePage = lazy(() => import('./pages/pipeline/RetailPipelinePage'));

const LoanDetailRoute = (
  <AppErrorBoundary>
    <Suspense fallback={<LoadingSkeleton />}>
      <LoanDetailPage />
    </Suspense>
  </AppErrorBoundary>
);

function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<LoadingSkeleton />}>
        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<LoginPage />} />

          {/* Public Routes */}
          <Route path="/auth/:userId/confirm" element={<ActivateAccountPage />} />
          <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
          <Route path="/signup" element={<BrokerSignupPage />} />

          {/* Lender / Admin Routes */}
          <Route path="/lender" element={
            <ProtectedRoute allowedRoles={[Roles.Admin, Roles.LenderAdmin]}>
              <AppLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Navigate to="/lender/dashboard" replace />} />
            <Route path="dashboard" element={<LenderDashboardPage />} />
            <Route path="brokers" element={<BrokersListPage />} />
            <Route path="brokers/:id" element={<BrokerDetailPage />} />
            <Route path="compensation-requests" element={<CompensationRequestsPage />} />
            <Route path="users" element={<LenderUsersPage />} />
            <Route path="vesta-contacts" element={<VestaContactSyncPage />} />
            <Route path="retail-pipeline" element={<RetailPipelinePage />} />
            <Route path="scenario-pricer" element={<ScenarioPricerPage />} />
            <Route path="retail-pipeline/:id" element={LoanDetailRoute} />
            <Route path="lock-desk" element={
              <ProtectedRoute allowedRoles={[Roles.Admin, Roles.LenderAdmin]}>
                <LockDeskSettingsPage />
              </ProtectedRoute>
            } />
            <Route path="branding" element={
              <ProtectedRoute allowedRoles={[Roles.Admin, Roles.LenderAdmin]}>
                <BrandingManagementPage />
              </ProtectedRoute>
            } />
            <Route path="feature-management" element={
              <ProtectedRoute allowedRoles={[Roles.Admin]}>
                <FeatureManagementPage />
              </ProtectedRoute>
            } />
            <Route path="marketing/*" element={
              <ProtectedRoute allowedRoles={[Roles.Admin, Roles.LenderAdmin]}>
                <MarketingAdminPage />
              </ProtectedRoute>
            } />
          </Route>

          {/* AE (Account Executive) Routes */}
          <Route path="/ae" element={
            <ProtectedRoute allowedRoles={[Roles.Admin, Roles.LenderAdmin, Roles.LoanOfficer, Roles.AccountExecutive]}>
              <AppLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Navigate to="/ae/dashboard" replace />} />
            <Route path="dashboard" element={<AeDashboardPage />} />
            <Route path="pipeline" element={<AeDashboardPage />} />
            <Route path="scenario-pricer" element={<ScenarioPricerPage />} />
            <Route path="loans/:id" element={LoanDetailRoute} />
            {/* Match nav: Book/Recruiting/Marketing/Scorecard/Resources are AccountExecutive-only */}
            <Route path="brokers" element={
              <ProtectedRoute allowedRoles={[Roles.AccountExecutive]}>
                <AeBookOfBusinessPage />
              </ProtectedRoute>
            } />
            <Route path="brokers/:id" element={
              <ProtectedRoute allowedRoles={[Roles.AccountExecutive]}>
                <AeBroker360Page />
              </ProtectedRoute>
            } />
            <Route path="prospects" element={
              <ProtectedRoute allowedRoles={[Roles.AccountExecutive]}>
                <AeComingSoonPage
                  title="Prospects"
                  subtitle="Recruiting pipeline"
                  description="Kanban stages, activity logging, and invite-to-apply will land with the AE CRM."
                />
              </ProtectedRoute>
            } />
            <Route path="marketing" element={
              <ProtectedRoute allowedRoles={[Roles.AccountExecutive]}>
                <AeComingSoonPage
                  title="Marketing"
                  subtitle="Content and campaigns for your book"
                  description="Library, compose, lists, and campaign results will replace the uff.loans marketing slot here."
                />
              </ProtectedRoute>
            } />
            <Route path="scorecard" element={
              <ProtectedRoute allowedRoles={[Roles.AccountExecutive]}>
                <AeComingSoonPage
                  title="Scorecard"
                  subtitle="Goals, production, and peer rank"
                  description="Goal attainment, pull-through, cycle time, and commission estimates will show here."
                />
              </ProtectedRoute>
            } />
            <Route path="resources" element={
              <ProtectedRoute allowedRoles={[Roles.AccountExecutive]}>
                <AeComingSoonPage
                  title="Resources"
                  subtitle="Playbooks and reference material"
                  description="AE playbooks and shared resources will be collected here."
                />
              </ProtectedRoute>
            } />
          </Route>

          {/* Broker Routes — read-only users: pipeline + loan view only; mutations require BrokerAdmin/Broker/Processor */}
          <Route path="/broker" element={
            <ProtectedRoute allowedRoles={BROKER_PORTAL_ROLES}>
              <AppLayout />
            </ProtectedRoute>
          }>
            <Route index element={<Navigate to="/broker/pipeline" replace />} />
            <Route path="company" element={
              <ProtectedRoute allowedRoles={[Roles.BrokerAdmin, Roles.Processor]}>
                <BrokerManageCompanyPage />
              </ProtectedRoute>
            } />
            <Route path="scenario-pricer" element={
              <ProtectedRoute allowedRoles={BROKER_PORTAL_ROLES}>
                <ScenarioPricerPage />
              </ProtectedRoute>
            } />
            <Route path="pipeline" element={<BrokerPipelinePage />} />
            <Route path="loans/:id" element={LoanDetailRoute} />
            <Route path="compensation" element={
              <ProtectedRoute allowedRoles={[Roles.BrokerAdmin]}>
                <CompensationPage />
              </ProtectedRoute>
            } />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}

export default App;
